import React from "react";
import {
  MicVocal, BarChart3, Brain, Search, ShieldCheck, FileText, Sparkles,
  MessageSquare, Activity, Zap,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, Lead, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  ConversationThread, FeatureCallout, FlowDiagram, ChecklistCard,
  SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogVoiceAiPlainLanguageInsights = () => (
  <RichBlogLayout
    category="Practice Intelligence"
    eyebrow="Voice + Insights"
    title="Stop Pulling Reports. Start Asking Questions."
    subtitle="How plain-language voice inside Asha replaces the digging, filtering, and report-rebuilding most practice management systems force you into."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Lead>
      "Hey Asha, can you analyze my claims from the last three months? Show me
      where I'm losing money and come up with a plan for my team."
    </Lead>

    <Prose>
      <p>
        That's a sentence a practice owner says out loud — once — and then walks
        away from. There is no report to build. No filter to set. No saved view
        to remember the name of. No teammate to ping at 9:41 PM asking if they
        can pull the data before tomorrow's huddle.
      </p>
      <p>
        It is a question. The system does the rest.
      </p>
      <p>
        That is a quietly enormous shift. Most practice management systems
        treat insight as an artifact — a thing you produce by combining three
        screens, four filters, and an export to Excel. Asha treats insight as a
        conversation. You ask. It thinks. It answers. And then it builds the
        plan.
      </p>

      <h2>The cost of "go look it up"</h2>
      <p>
        Walk through a typical week at any growing practice and count how often
        the answer to a real operational question is "let me pull a report."
        Where are we losing on hygiene fall-off? Which carrier is slow-paying
        us right now? Why did production dip last Thursday? Each of those
        questions, in most PMS software, is a 20-minute scavenger hunt — if you
        even know which screen to start on.
      </p>
      <p>
        The hidden tax isn't the time. It's the questions you stop asking
        because the friction is too high. The decisions you make on instinct
        because the data was buried three layers deep. That's where revenue
        quietly leaks.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 1, label: "Step from question to answer" },
        { value: 0, label: "Reports to build first" },
        { value: 20, suffix: " min", label: "Saved per ad-hoc question" },
        { value: 100, suffix: "%", label: "Live chart data, not stale exports" },
      ]}
    />

    <Prose>
      <p>
        Asha collapses the loop. The question becomes the workflow. And the
        answer comes back not as a CSV — but as a narrative with a recommended
        plan you can hand directly to your team.
      </p>

      <h2>Dictation types your words. Asha understands them.</h2>
      <p>
        Every PMS on the market eventually adds "voice." What they almost
        always mean is dictation — speech-to-text that types whatever sound
        comes out of your mouth. Pause for a second, change your mind mid-
        sentence, rephrase a number, and the transcript turns into a mess.
      </p>
      <p>
        Asha works differently. It listens to the full sentence in real time
        and interprets meaning, not just words. So when you talk the way humans
        actually talk — fast, a little unstructured, correcting yourself,
        circling a thought — the system follows what you mean.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Word-for-word dictation"
      afterTitle="Contextual voice understanding"
      beforeItems={[
        "Types every disfluency, pause, and false start verbatim",
        "Misses meaning when you correct yourself mid-sentence",
        "Forces you to speak in a stilted, pre-edited way",
        "You stop and clean up the transcript constantly",
      ]}
      afterItems={[
        "Listens to the whole sentence and interprets the intent",
        "Auto-corrects context as you speak — not after the fact",
        "Lets you talk fast, rephrase, and be naturally unstructured",
        "Turns the spoken question into an executable analysis task",
      ]}
    />

    <Prose>
      <h2>What it actually does when you ask</h2>
      <p>
        The "ask a question" surface looks like a sentence in a search bar. The
        substance under it is a pipeline. Asha parses the intent, identifies
        the right entities (claims, providers, payers, time windows), runs the
        analysis across your live chart data, and writes back a structured
        result with a plan.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "You ask in plain language",
          body:
            "Speak or type a question the way you'd say it to a chief of staff. No template, no field selector, no saved view to load.",
        },
        {
          title: "Asha interprets meaning, not words",
          body:
            "Voice understanding listens to the full sentence in real time. If you pause, correct, or rephrase, Asha follows the intent — not the audio.",
        },
        {
          title: "It identifies the analysis task",
          body:
            "The system maps the question to the right slice of data: which claims, which window, which payers, which providers, which procedures.",
        },
        {
          title: "It runs the work across live data",
          body:
            "No exports, no stale snapshots. Asha pulls directly from the chart, the ledger, the schedule, and the claims log as they exist right now.",
        },
        {
          title: "It writes back a narrative + a plan",
          body:
            "You get a clear answer in plain English plus an actionable plan you can hand to your team — not a spreadsheet you still have to interpret.",
        },
      ]}
    />

    <PullQuote>
      The PMS stops being a place you go to pull reports. It becomes something
      you can ask a question — and get a plan back, already routed to the team.
    </PullQuote>

    <Prose>
      <h2>A real morning, asked out loud</h2>
      <p>
        It's 7:42 AM. The owner walks into the operatory, coffee in hand, and
        speaks into Asha. No spreadsheet open. No login screen for an analytics
        tool. Just a sentence.
      </p>
    </Prose>

    <ConversationThread
      title="Owner morning briefing · plain-language ask"
      channel="Voice"
      messages={[
        { from: "system", text: "Voice session opened · 7:42 AM" },
        { from: "patient", text: "Hey Asha, can you analyze my claims from the last three months and show me where I'm losing money? And come up with a plan for the team." },
        { from: "agent", text: "Looking at the last 90 days. I see $14,820 in net write-offs across 87 claims, with 62% concentrated in three patterns: ortho installment denials, missing narratives on D2750, and frequency-limit conflicts on D1110." },
        { from: "agent", text: "Suggested plan: route the 11 open ortho denials to Sarah for appeal drafting today, attach narrative templates for the 14 D2750 claims pending submission, and flag 9 hygiene visits to verify against the carrier's frequency rule before Friday." },
        { from: "patient", text: "Good. Send that to the team and put the ortho ones at the top." },
        { from: "system", text: "Plan published to morning huddle · Tasks routed · Sarah notified" },
      ]}
    />

    <Prose>
      <p>
        That whole exchange takes less than ninety seconds. In a traditional
        PMS, the equivalent is: open Reports, choose Claims by Status, set the
        date filter, export to CSV, sort by adjustment reason, copy the top
        denials, paste into a doc, write up a plan, email the team. An hour,
        easily — if it gets done at all.
      </p>

      <h2>Why the voice layer matters more than the chat layer</h2>
      <p>
        It's tempting to think of this as "ChatGPT for your PMS." That misses
        the point. The reason this works is the voice understanding underneath.
        In a clinical or operational setting, you don't have hands free to type
        out a precise prompt. You have thirty seconds between patients. You're
        walking from op 3 to the front. You're dictating in fragments while
        your team is moving around you.
      </p>
      <p>
        If the system can't follow natural speech — including the pauses, the
        "wait, scratch that," the "actually, just for Delta patients" — it
        won't get used. Voice understanding is the difference between an
        accessible insight layer and a feature that lives in a sidebar nobody
        opens.
      </p>
    </Prose>

    <ChecklistCard
      title="What Asha understood from one spoken sentence"
      items={[
        { label: "Intent: financial analysis of claims", status: "pass" },
        { label: "Time window: last three months", status: "pass" },
        { label: "Goal: identify revenue leakage drivers", status: "pass" },
        { label: "Output: narrative + delegated action plan", status: "pass" },
        { label: "Speech disfluencies auto-corrected", status: "pass" },
      ]}
    />

    <Prose>
      <h2>Accuracy and speed are the same thing</h2>
      <p>
        In a clinical or operational setting, every fix costs something. If you
        have to stop and correct what the system misheard, you lose time. Over
        a day that adds up. Over a week, it's the difference between a tool
        that gets used and a tool that gets quietly abandoned.
      </p>
      <p>
        By interpreting context as you speak — instead of blindly typing what
        you said — Asha removes that friction completely. The software adapts
        to how you naturally communicate. You don't adapt to the software.
      </p>
    </Prose>

    <FlowDiagram
      title="From spoken question to delegated plan"
      nodes={[
        { label: "Spoken question", sublabel: "Natural, unstructured", icon: MicVocal },
        { label: "Intent + context", sublabel: "Real-time understanding", icon: Brain },
        { label: "Live data analysis", sublabel: "Across chart + claims", icon: BarChart3 },
        { label: "Plan published", sublabel: "Routed to the team", icon: Sparkles },
      ]}
    />

    <FeatureCallout
      stat="Ask → Answer"
      label="One step instead of seven"
      description="A spoken question becomes a complete analysis and an action plan. No reports to build, no filters to remember, no exports to clean up."
      icon={MicVocal}
    />

    <Prose>
      <h2>How owners, managers, and clinicians use this differently</h2>
      <p>
        For owners, it changes what gets asked. The questions you only used to
        bring up at the quarterly review now happen during morning huddle.
        "How's our case acceptance trending this month?" becomes a sentence,
        not a project.
      </p>
      <p>
        For office managers, it ends the role of human BI analyst. The afternoons
        spent rebuilding the same report three different ways for three
        different stakeholders disappear. The work moves from extracting data
        to acting on it.
      </p>
      <p>
        And for clinicians, it means the system finally meets you where you
        already work — voice-first, fast, forgiving, and tied to the chart.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        The biggest unlock isn't the speed of the answer. It's the questions
        you finally start asking — because the cost of asking has gone to zero.
        That's what it means to stop pulling reports and start talking to your
        practice.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogVoiceAiPlainLanguageInsights;
